'use client';
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, Dialog, DialogTitle, DialogContent,
  InputAdornment, MenuItem, Avatar, Chip, Pagination, TableSortLabel, Select,
  Grid, useTheme
} from '@mui/material';
import { Helmet } from 'react-helmet';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from '@mui/icons-material/Add';
import { useUser } from "../context/UserContext";
import SectionLoader from '../components/SectionLoader';
import ConfirmDialog from "../components/ConfirmDialog";
import Toast from "../components/Toast";
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArchiveManager from "../utils/archiveManager";
const PrayerCategoryList = ({ hideHeader = false }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [categories, setCategories] = useState([]);
  const [selectedCategoryForView, setSelectedCategoryForView] = useState(null);
  const [categoryDetailModalOpen, setCategoryDetailModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [editCategory, setEditCategory] = useState(null);
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    effectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);
  const [addCategory, setAddCategory] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('prayer-categories'));
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('prayer-categories'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');

  // Auto-save draft on unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (addCategory && addCategory.name && addCategory.name.trim()) {
        DraftManager.saveDraft('prayer-categories', { name: addCategory.name }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [addCategory, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!addCategory || !addCategory.name || !addCategory.name.trim()) {
      setToast({ open: true, message: "Please enter a category name before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('prayer-categories', { name: addCategory.name }, activeDraftId);
    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Category draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    setAddCategory({ name: draft.data.name || '' });
    setActiveDraftId(draft.id);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('prayer-categories', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleArchive = (cat) => {
    setConfirmDialog({
      open: true,
      title: "Archive Prayer Category",
      message: "Are you sure you want to archive this category? It will no longer be visible to mobile app users until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('prayer-categories', cat);
        setArchivedList(ArchiveManager.getArchivedItems('prayer-categories'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Category archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (catId) => {
    ArchiveManager.restoreItem('prayer-categories', catId);
    setArchivedList(ArchiveManager.getArchivedItems('prayer-categories'));
    setToast({ open: true, message: "Category restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (catId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Category",
      message: "Are you sure you want to permanently delete this archived category? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('prayer-categories', catId);
        setArchivedList(ArchiveManager.getArchivedItems('prayer-categories'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Category permanently removed.", severity: "success" });
      }
    });
  };

  // Fetch category data (mock or API)
  useEffect(() => {
    fetchCategories();
  }, [status, page, organization, search, fromDate, toDate]);


  /////////// Fetch Categories ///////////
  const fetchCategories = async () => {
    // Replace this with your actual API call

    try {
      setLoading(true);
      const targetOrgId = effectiveOrgId || (organization && organization.id !== 0 ? organization.id : null);

      const response = await fetch(`${API_URL}get-prayer-categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: page,
          status: status,
          organization: targetOrgId || null,
          search: search,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        })
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
      }

      setCategories(data.data.categories);
      setOrganizations(data.data.organisations);
      setTotalPages(data.data.totalPages);

    } catch (err) {
      // setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  /////////// Edit Category ///////////
  const handleEdit = (category) => {
    setEditCategory(category);
    // Handle edit action
    console.log('Edit', category);
  };

  ////////// Delete Category ///////////
  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category? If you delete this category, all prayers associated with it will also be deleted. This action cannot be undone.',
      confirmText: 'Confirm Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));

        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}delete-prayer-category`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: id,
              status: 3
            })
          });
          const data = await response.json();

          if (!response.ok || data.success === false) {
            if (data.message === 'Invalid token') {
              localStorage.removeItem('token');
              window.location.href = '/login';
              return;
            }
            setToast({ open: true, message: "Delete failed: " + (data?.message || 'Unknown error'), severity: "error" });
            return;
          }

          setCategories(prev => prev.filter(c => String(c.id) !== String(id)));
          setToast({ open: true, message: "Category and associated prayers deleted successfully", severity: "success" });
          fetchCategories();
        } catch (err) {
          setToast({ open: true, message: "Delete failed. Please try again.", severity: "error" });
        }
      }
    });
  };

  //////////////// Update Category ///////////

  const handleSaveEdit = async () => {
    if (editCategory.name === undefined || editCategory.name.trim() === '') {
      setToast({ open: true, message: 'Category is required', severity: "error" });
      return false;
    }
    const token = localStorage.getItem('token');

    const response = await fetch(`${API_URL}update-prayer-category`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // ❌ Do NOT set 'Content-Type' when sending FormData
      },
      body: JSON.stringify({
        id: editCategory.id,
        name: editCategory.name,
      })
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: "Delete failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }

      setToast({ open: true, message: "Category updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
  
  };


  /////////// Add Category ///////////
  const handleSaveAdd = async () => {
    if (addCategory.name === undefined || addCategory.name.trim() === '') {
      setToast({ open: true, message: 'Category is required', severity: "error" });
      return false;
    }
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}add-prayer-category`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: addCategory.name,
      })
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: "Delete failed: " + (data?.message || 'Unknown error'), severity: "error" });
    }
      setToast({ open: true, message: "Category added successfully", severity: "success" });
      if (activeDraftId) {
        DraftManager.deleteDraft('prayer-categories', activeDraftId);
        setActiveDraftId(null);
        setDraftsList(DraftManager.getDrafts('prayer-categories'));
      }
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    
  };




  const displayedCategories = useMemo(() => {
    const list = filterActiveTab === "archived"
      ? archivedList
      : categories.filter(c => !archivedList.some(a => String(a.id) === String(c.id)));
    return [...list].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase().trim();
      const nameB = (b.name || '').toLowerCase().trim();
      const isOthersA = nameA === 'others' || nameA === 'other';
      const isOthersB = nameB === 'others' || nameB === 'other';
      if (isOthersA && !isOthersB) return 1;
      if (!isOthersA && isOthersB) return -1;
      if (sortOrder === 'asc') return nameA.localeCompare(nameB);
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [categories, archivedList, filterActiveTab, sortOrder]);

  return (

    <>
      <Helmet>
        <title>Prayer Categories - Glocal Prayer Network</title>
      </Helmet>
      <Box sx={{ p: hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }, pt: hideHeader ? 0 : undefined }}>
        {!hideHeader && (
          <Box mb={3}>
            <Typography variant="h5" fontWeight={700}>Prayer Category Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage prayer categories, including name, status, and organization.
            </Typography>
          </Box>
        )}



        {/* Search + Filter */}

        <Paper sx={{ p: 2, border: "1px solid #E5E7EB" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2
            }}
          >
            {/* Heading */}
            <Typography variant="h6" fontWeight={700}>
              Prayer Categories
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <TextField
                size="small"
                placeholder="Search category"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ width: 350 }}
              />

              <TextField
                size="small"
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => {
                  const val = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  if (val > today) {
                    setToast({ open: true, message: "Cannot select future dates", severity: "warning" });
                    return;
                  }
                  setFromDate(val);
                  setPage(1);
                }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                sx={{ width: 160 }}
              />

              <TextField
                size="small"
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => {
                  const val = e.target.value;
                  const today = new Date().toISOString().split('T')[0];
                  if (val > today) {
                    setToast({ open: true, message: "Cannot select future dates", severity: "warning" });
                    return;
                  }
                  setToDate(val);
                  setPage(1);
                }}
                inputProps={{ max: new Date().toISOString().split('T')[0], min: fromDate || undefined }}
                sx={{ width: 160 }}
              />

              <TextField
                label="Status"
                variant="outlined"
                size="small"
                select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
              </TextField>

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Organization" />}
                  sx={{ width: 230 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}

              <Button
                variant={filterActiveTab === "archived" ? "contained" : "outlined"}
                startIcon={<InventoryIcon />}
                onClick={() => setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived")}
                sx={{
                  color: filterActiveTab === "archived" ? '#fff' : '#64d2e2',
                  borderColor: 'rgba(76, 161, 175, 0.4)',
                  backgroundColor: filterActiveTab === "archived" ? '#177373' : 'transparent',
                  '&:hover': {
                    backgroundColor: filterActiveTab === "archived" ? '#135e5e' : 'rgba(76, 161, 175, 0.15)',
                  }
                }}
              >
                Archived ({archivedList.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<DraftsIcon />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('prayer-categories'));
                  setDraftsOpen(true);
                }}
                sx={{
                  borderColor: 'rgba(255, 183, 77, 0.5)',
                  color: '#ffb74d',
                  '&:hover': {
                    borderColor: '#ffb74d',
                    backgroundColor: 'rgba(255, 183, 77, 0.1)',
                  }
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => setAddCategory(true)} sx={{ background: '#177373' }}>Add Category</Button>


            </Box>
          </Box>
        </Paper>


        {/* Category Table */}
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', borderRadius: '16px' }}>
          <Table sx={{ minWidth: { xs: 550, md: 700 } }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={true}
                    direction={sortOrder}
                    onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell>Active Prayers</TableCell>
                {showOrganizationColumn && (
                  <TableCell>Organization</TableCell>
                )}
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              {loading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                    <div style={{ position: "relative", minHeight: 200 }}>
                      <SectionLoader open={loading} height={200} />
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {displayedCategories.map((cat) => (
                <TableRow
                  key={cat.id}
                  hover
                  onClick={() => {
                    setSelectedCategoryForView(cat);
                    setCategoryDetailModalOpen(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: isLight ? 'rgba(13, 148, 136, 0.06) !important' : 'rgba(76, 161, 175, 0.12) !important' },
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: "#f3f4f6",
                          color: "#2C3E50",
                          fontWeight: 700,
                          fontSize: 20,
                        }}
                      >
                        {cat.name?.[0]?.toUpperCase() || "?"}
                      </Avatar>
                      {/* <Avatar src={cat.iconUrl} /> */}
                      {cat.name}
                    </Box>
                  </TableCell>
                  <TableCell>{cat._count ? cat._count.pray_requests : 0}</TableCell>
                  {showOrganizationColumn && (
                    <TableCell>{cat.origanisation ? cat.origanisation.org_name : 'Default Category'}</TableCell>
                  )}
                  <TableCell>

                    <Chip
                      label={cat.deleted_at ? 'Deleted' : cat.status || 'active'}
                      color={cat.deleted_at ? 'error' : cat.status === 'active' ? 'success' : cat.status === 'inactive' ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {cat.created_at
                      ? new Date(cat.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                      })
                      : ""}
                  </TableCell>

                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {filterActiveTab === "archived" ? (
                      <Box display="flex" gap={1}>
                        <Tooltip title="Restore to active (Make visible in mobile app)">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handleRestore(cat.id); }}
                            sx={{ color: '#4CAF50', '&:hover': { background: 'rgba(76, 175, 80, 0.1)' } }}
                          >
                            <UnarchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Permanently delete">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); handlePermanentDelete(cat.id); }}
                            sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : cat.deleted_at != null ? (
                      <Typography variant="body2" color="textSecondary">Deleted</Typography>
                    ) : (
                      <Box display="flex" gap={0.5} alignItems="center">
                        <Tooltip title="Edit Category">
                          <IconButton
                            aria-label="edit category"
                            onClick={(e) => { e.stopPropagation(); handleEdit(cat); }}
                            sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Archive (Hide from mobile app)">
                          <IconButton
                            aria-label="archive category"
                            onClick={(e) => { e.stopPropagation(); handleArchive(cat); }}
                            sx={{ color: '#ffb74d', '&:hover': { background: 'rgba(255, 183, 77, 0.1)' } }}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete Category">
                          <IconButton
                            aria-label="delete category"
                            onClick={(e) => { e.stopPropagation(); handleDelete(cat.id); }}
                            sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && displayedCategories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {filterActiveTab === "archived" ? "No archived categories found" : "No categories found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
          />
        </Box>


        {editCategory && (
          <Dialog open={!!editCategory} onClose={() => setEditCategory(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Category</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                  <TextField label="Category Name" value={editCategory.name} onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })} />
                </Box>

              </Box>



              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
                <Button variant="text" onClick={() => setEditCategory(null)} sx={{ marginLeft: 2 }}>Cancel</Button>
              </Box>


            </DialogContent>
          </Dialog>
        )}
        {addCategory && (
          <Dialog open={!!addCategory} onClose={() => setAddCategory(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Add Category</span>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DraftsIcon />}
                  onClick={() => {
                    setDraftsList(DraftManager.getDrafts('prayer-categories'));
                    setDraftsOpen(true);
                  }}
                  sx={{
                    borderColor: 'rgba(255, 183, 77, 0.5)',
                    color: '#ffb74d',
                    fontSize: '0.75rem',
                  }}
                >
                  Drafts ({draftsList.length}/3)
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleManualSaveDraft}
                  sx={{
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                    color: '#64d2e2',
                    fontSize: '0.75rem',
                  }}
                >
                  Save Draft
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField fullWidth label="Category Name" value={addCategory.name || ''} onChange={(e) => setAddCategory({ ...addCategory, name: e.target.value })} />
                  <HelpTooltip
                    title="Prayer Category Name"
                    purpose="Label classifying prayers into focus areas (e.g., Healing, Nation, Church)"
                    howItWorks="Members select this category when requesting or filtering prayers"
                    mobileEffect="Visible on mobile as category filter chips and form selector"
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button variant="contained" onClick={handleSaveAdd} sx={{ background: '#177373' }}>Save</Button>
                <Button variant="text" onClick={() => setAddCategory(false)} sx={{ marginLeft: 2 }}>Cancel</Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}

        {/* Category Detail Modal */}
        {categoryDetailModalOpen && selectedCategoryForView && (
          <Dialog
            open={categoryDetailModalOpen}
            onClose={() => {
              setCategoryDetailModalOpen(false);
              setSelectedCategoryForView(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                bgcolor: isLight ? "#FFFFFF" : "#1e293b",
                color: isLight ? "#0F172A" : "#FFFFFF",
                boxShadow: isLight ? "0 20px 40px rgba(0,0,0,0.15)" : "0 20px 40px rgba(0,0,0,0.5)",
              }
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.1)",
                pb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CategoryIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF" }} />
                <Typography variant="h6" fontWeight={700}>
                  Prayer Category Details
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setCategoryDetailModalOpen(false);
                  setSelectedCategoryForView(null);
                }}
                sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F0FDFA" : "rgba(13, 148, 136, 0.2)",
                      color: isLight ? "#0D9488" : "#64d2e2",
                      border: isLight ? "2px solid #CCFBF1" : "2px solid rgba(76, 161, 175, 0.3)",
                      fontWeight: 700,
                      fontSize: "1.5rem",
                    }}
                  >
                    {selectedCategoryForView.name?.[0]?.toUpperCase() || "?"}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedCategoryForView.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Chip
                        label={selectedCategoryForView.deleted_at ? "Deleted" : selectedCategoryForView.status || "active"}
                        color={
                          selectedCategoryForView.deleted_at
                            ? "error"
                            : selectedCategoryForView.status === "active"
                              ? "success"
                              : "default"
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={`${selectedCategoryForView._count ? selectedCategoryForView._count.pray_requests : 0} prayer requests`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          color: isLight ? "#0D9488" : "#64d2e2",
                          borderColor: isLight ? "#0D9488" : "rgba(76, 161, 175, 0.4)",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.08)",
                  }}
                >
                  <Grid container spacing={2}>
                    {showOrganizationColumn && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                          Organization
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedCategoryForView.origanisation?.org_name || "Default Category"}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Created Date
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedCategoryForView.created_at
                          ? new Date(selectedCategoryForView.created_at).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric"
                            })
                          : "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
                <Button
                  onClick={() => {
                    setCategoryDetailModalOpen(false);
                    setSelectedCategoryForView(null);
                  }}
                  sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)", textTransform: "none" }}
                >
                  Close
                </Button>
                {((Number(selectedCategoryForView.org_id) === Number(user.org_id)) || user.role === 1) && !selectedCategoryForView.deleted_at && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      const catToEdit = selectedCategoryForView;
                      setCategoryDetailModalOpen(false);
                      setSelectedCategoryForView(null);
                      handleEdit(catToEdit);
                    }}
                    sx={{
                      background: isLight ? "#0D9488" : "#177373",
                      color: "#FFFFFF",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { background: isLight ? "#0F766E" : "#135d5d" }
                    }}
                  >
                    Edit Category
                  </Button>
                )}
              </Box>
            </DialogContent>
          </Dialog>
        )}

      </Box>
      <Toast toast={toast} setToast={setToast} />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText || "Confirm Delete"}
        cancelText={confirmDialog.cancelText || "Cancel"}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        severity="error"
      />
      {/* Floating Settings Icon */}
      {!hideHeader && (
        <Tooltip title="Prayer Categories Settings" placement="left">
          <IconButton
            onClick={() => setSettingsOpen(true)}
            sx={{
              position: 'fixed', bottom: 32, right: 32,
              bgcolor: 'rgba(76, 161, 175, 0.15)', border: '1px solid rgba(76, 161, 175, 0.4)',
              color: '#64d2e2', width: 48, height: 48, zIndex: 1300, backdropFilter: 'blur(8px)',
              '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
              transition: 'all 0.25s ease',
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      )}
      <ModuleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} moduleKey="prayer-categories" moduleTitle="Prayer Categories" />
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        moduleKey="prayer-categories"
        moduleTitle="Prayer Categories"
        onContinueDraft={handleContinueDraft}
      />
    </>
  );
};

export default PrayerCategoryList;
